import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { DragIndicator as DragHandleIcon } from '@mui/icons-material';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { ColumnConfigItem } from '../../../types/lead';
import { SortableItemActions } from './SortableItemActions';

interface SortableItemViewProps {
  setNodeRef: (element: HTMLElement | null) => void;
  column: ColumnConfigItem;
  style: React.CSSProperties;
  attributes: Record<string, any>;
  listeners: SyntheticListenerMap | undefined;
  dataTypeLabel?: string;
  isDragging: boolean;
  isHighlighted?: boolean;
  onToggleVisibility: (key: string) => void;
  onRename?: (key: string) => void;
  onDeactivate?: (key: string) => void;
}

export const SortableItemView: React.FC<SortableItemViewProps> = React.memo(({
  setNodeRef,
  column,
  style,
  attributes,
  listeners,
  dataTypeLabel,
  isDragging,
  isHighlighted,
  onToggleVisibility,
  onRename,
  onDeactivate,
}) => {
  return (
    <Box
      ref={setNodeRef}
      id={`column-item-${column.key}`}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        mb: 1,
        border: isDragging ? '1px solid #1976d2' : (isHighlighted ? '1px solid #90caf9' : '1px solid #e0e0e0'),
        borderRadius: 1,
        backgroundColor: isDragging ? '#ffffff' : (isHighlighted ? '#e8f0fe' : '#fff'),
        boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
        willChange: 'transform',
        userSelect: 'none',
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
          flex: 1,
          mr: 1,
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            p: 0.5,
            m: -0.5,
            borderRadius: 0.5,
            flexShrink: 0,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <DragHandleIcon fontSize="small" />
        </Box>

        <Tooltip title={column.label} enterTouchDelay={0}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: column.is_default ? 600 : 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {column.label}
          </Typography>
        </Tooltip>

        {column.is_default && (
          <Chip
            label="Default"
            size="small"
            color="default"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.75rem',
              flexShrink: 0,
              '& .MuiChip-label': {
                px: 1,
                textAlign: 'center',
                fontSize: '0.75rem',
              },
            }}
          />
        )}

        {dataTypeLabel && (
          <Chip
            label={dataTypeLabel}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.75rem',
              fontWeight: 500,
              flexShrink: 0,
              bgcolor: 'rgba(25, 118, 210, 0.06)',
              '& .MuiChip-label': {
                px: 1,
                textAlign: 'center',
                fontSize: '0.75rem',
              },
            }}
          />
        )}
      </Box>

      <SortableItemActions
        column={column}
        onToggleVisibility={onToggleVisibility}
        onRename={onRename}
        onDeactivate={onDeactivate}
      />
    </Box>
  );
});
